// Column + group model and client-side filter logic for the project images table
// (design: IMS Images Page). Column keys equal the row-field keys so no remapping is
// needed: image-native fields + the flat case-information values (see case-information.js).
// labelKey / labelKey-on-group are i18n keys resolved in the component with $t.

export const COLUMN_GROUPS = [
  {id: 'image', labelKey: 'column-group-image'},
  {id: 'case', labelKey: 'case-information-section-case'},
  {id: 'patient', labelKey: 'case-information-section-patient'},
  {id: 'doctor', labelKey: 'case-information-section-doctor'},
  {id: 'clinic', labelKey: 'case-information-section-clinic'},
  {id: 'specimen', labelKey: 'case-information-section-specimen'},
  {id: 'slide', labelKey: 'case-information-section-slide'},
];

// type: text | number | date | image | link
export const COLUMNS = [
  // Image
  {key: 'overview', labelKey: 'overview', group: 'image', type: 'image', mandatory: true, defaultVisible: true, sortable: false, filterable: false, align: 'left', width: 116},
  {key: 'name', labelKey: 'name', group: 'image', type: 'link', mandatory: true, defaultVisible: true, sortable: true, filterable: true, align: 'left', minWidth: 220},
  {key: 'magnification', labelKey: 'magnification', group: 'image', type: 'number', defaultVisible: false, sortable: true, filterable: true, align: 'right', width: 150},
  {key: 'manualAnnotations', labelKey: 'manual-annotations', group: 'image', type: 'number', defaultVisible: false, sortable: true, filterable: true, align: 'right', width: 170},
  {key: 'reviewedAnnotations', labelKey: 'reviewed-annotations', group: 'image', type: 'number', defaultVisible: false, sortable: true, filterable: true, align: 'right', width: 185},
  {key: 'format', labelKey: 'format', group: 'image', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 120},

  // Case
  {key: 'caseId', labelKey: 'case-information-field-case-id', group: 'case', type: 'text', defaultVisible: true, sortable: true, filterable: true, align: 'left', width: 150},
  {key: 'caseClinicalDiagnosis', labelKey: 'case-information-field-clinical-diagnosis', group: 'case', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 190},
  {key: 'caseEntryDateTime', labelKey: 'case-information-field-entry', group: 'case', type: 'date', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 170},
  {key: 'caseSamplingDateTime', labelKey: 'case-information-field-sampling', group: 'case', type: 'date', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 170},

  // Patient
  {key: 'patientFullName', labelKey: 'patient-name', group: 'patient', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 180},
  {key: 'patientDob', labelKey: 'case-information-field-patient-dob', group: 'patient', type: 'date', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 160},
  {key: 'patientSex', labelKey: 'case-information-field-patient-sex', group: 'patient', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 90},
  {key: 'patientAge', labelKey: 'case-information-field-patient-age', group: 'patient', type: 'number', defaultVisible: false, sortable: true, filterable: true, align: 'right', width: 90},

  // Doctor
  {key: 'doctorReferringName', labelKey: 'case-information-field-doctor-referring-name', group: 'doctor', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 190},
  {key: 'doctorPrimaryName', labelKey: 'case-information-field-doctor-primary-name', group: 'doctor', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 190},

  // Clinic
  {key: 'clinicName', labelKey: 'case-information-field-clinic-name', group: 'clinic', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 170},
  {key: 'departmentName', labelKey: 'case-information-field-department-name', group: 'clinic', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 160},

  // Biopsy / Specimen
  {key: 'specimenId', labelKey: 'case-information-field-specimen-id', group: 'specimen', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 150},
  {key: 'specimenType', labelKey: 'case-information-field-specimen-type', group: 'specimen', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 160},
  {key: 'specimenDetails', labelKey: 'case-information-field-specimen-details', group: 'specimen', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 180},
  {key: 'specimenMacroscopicDescription', labelKey: 'case-information-field-specimen-macroscopic-description', group: 'specimen', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 200},
  {key: 'specimenBiopsyInfo', labelKey: 'case-information-field-biopsy-info', group: 'specimen', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 180},

  // Slide
  {key: 'slideId', labelKey: 'case-information-field-slide-id', group: 'slide', type: 'text', defaultVisible: true, sortable: true, filterable: true, align: 'left', width: 160},
  {key: 'slideExternalId', labelKey: 'case-information-field-slide-external-id', group: 'slide', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 180},
  {key: 'slideBlockId', labelKey: 'case-information-field-block-id', group: 'slide', type: 'text', defaultVisible: false, sortable: true, filterable: true, align: 'left', width: 140},
];

export const COLUMN_BY_KEY = COLUMNS.reduce((m, c) => {
  m[c.key] = c;
  return m;
}, {});

// operator ids are stable; their visible labels come from i18n (OP_LABEL_KEY).
const OPERATORS = {
  text: ['contains', 'not-contains', 'is', 'is-not', 'starts-with', 'is-empty'],
  link: ['contains', 'is', 'is-not', 'starts-with'],
  number: ['is', 'is-not', 'gt', 'lt'],
  date: ['is', 'before', 'after'],
};

export const OP_LABEL_KEY = {
  'contains': 'op-contains',
  'not-contains': 'op-not-contains',
  'is': 'op-is',
  'is-not': 'op-is-not',
  'starts-with': 'op-starts-with',
  'is-empty': 'op-is-empty',
  'gt': 'op-greater-than',
  'lt': 'op-less-than',
  'before': 'op-before',
  'after': 'op-after',
};

export function opsFor(type) {
  return OPERATORS[type] || OPERATORS.text;
}

let _fid = 0;
export function makeFilter(column, conj) {
  const c = COLUMN_BY_KEY[column];
  return {id: `f${_fid++}`, column, operator: opsFor(c.type)[0], value: '', conj: conj || 'and'};
}

export function initialVisible() {
  const v = {};
  COLUMNS.forEach(c => {
    v[c.key] = !!c.defaultVisible || c.mandatory;
  });
  return v;
}

// single (row, column, operator, value) predicate — mirrors the design's matchOne.
export function matchOne(row, col, operator, value) {
  const raw = row[col.key];
  if (operator === 'is-empty') {
    return raw === '' || raw === null || raw === undefined;
  }
  if (value === '' || value === null || value === undefined) {
    return true; // an unset filter passes
  }
  const cell = (raw === null || raw === undefined) ? '' : String(raw).toLowerCase();
  const val = String(value).toLowerCase();

  if (col.type === 'number') {
    const n = Number(raw);
    const q = Number(value);
    switch (operator) {
      case 'is': return n === q;
      case 'is-not': return n !== q;
      case 'gt': return n > q;
      case 'lt': return n < q;
      default: return cell.includes(val);
    }
  }
  if (col.type === 'date') {
    const a = new Date(raw);
    const b = new Date(value);
    switch (operator) {
      case 'before': return a < b;
      case 'after': return a > b;
      default: return a.toDateString() === b.toDateString();
    }
  }
  switch (operator) {
    case 'is': return cell === val;
    case 'is-not': return cell !== val;
    case 'not-contains': return !cell.includes(val);
    case 'starts-with': return cell.startsWith(val);
    default: return cell.includes(val); // contains
  }
}
